import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

// Real client jobs. Already labeled in-image with BEFORE / AFTER.
// Order is deliberate: lead with the highest-impact transformation (garage),
// then interior (basement), then exterior (yard debris).
const GALLERY = [
  {
    id: 'garage-cleanout',
    src: '/ohana/portfolio/garage-cleanout.png',
    alt: 'Two-car garage cleanout, before and after. Before image shows the garage stacked floor-to-ceiling with furniture, mattresses, appliances, paint cans, and boxes. After image shows the same garage completely empty and broom-clean.',
    label: 'Two-Car Garage Cleanout',
    location: 'Columbus, Indiana',
    body:
      'A two-car garage stacked floor-to-ceiling with mattresses, furniture, appliances, paint cans, and years of accumulated boxes. Cleared in a single day. Usable items routed to donation through the Hearth Project; the rest taken to the Bartholomew County Landfill. Property handed back broom-clean and ready for the next chapter.',
    services: ['Property Cleanout', 'Furniture Removal', 'Appliance Removal'],
  },
  {
    id: 'basement-storage',
    src: '/ohana/portfolio/basement-storage.jpeg',
    alt: 'Basement storage room cleanout, before and after. Before image shows accumulated boxes, a bar chair, art supplies, and household goods stacked against the wall. After image shows the same basement room completely empty.',
    label: 'Basement Storage Cleanout',
    location: 'Central Indiana',
    body:
      'A basement storage corner full of boxes, household goods, and years of stored items. Emptied and prepped so the homeowner could refinish the space. Donation-conscious sort with everything usable routed to local partners.',
    services: ['Property Cleanout', 'Donation-Conscious Recovery'],
  },
  {
    id: 'basement-pool-table',
    src: '/ohana/portfolio/basement-pool-table.jpeg',
    alt: 'Basement cleanout with antique pool table removal, before and after. Before image shows a heavy upright pool table, bookshelves of books, holiday storage bins, and household goods. After image shows the same basement empty with a clean concrete floor.',
    label: 'Estate Basement Cleanout',
    location: 'Bartholomew County',
    body:
      'Estate cleanout including a heavy upright pool table, full bookshelves, holiday storage bins, and decades of family belongings. Sorted on-site with the family. Keepsakes set aside, usable items routed to donation, the rest disposed of through licensed channels.',
    services: ['Estate Cleanout', 'Furniture Removal', 'Property Cleanout'],
  },
  {
    id: 'yard-debris',
    src: '/ohana/portfolio/yard-debris.jpeg',
    alt: 'Yard debris removal, before and after. Before image shows a large pile of dead branches, brush, and yard debris in the backyard with a garbage truck in the background. After image shows a clean, mowed lawn with the debris gone.',
    label: 'Yard Debris & Brush Removal',
    location: 'Central Indiana',
    body:
      'A large pile of dead branches and brush hauled out and disposed of so the homeowner had a clean, usable backyard again. Yard waste handled through the appropriate Indiana recycling channel.',
    services: ['Yard Waste Removal', 'Debris Removal'],
  },
];

export default function Portfolio() {
  return (
    <section className="section-pad bg-ohana-surface" data-testid="portfolio-section">
      <div className="container-ohana">
        <div className="max-w-3xl">
          <p className="overline">Before / After</p>
          <h2
            className="h-display text-4xl md:text-5xl mt-3 leading-tight"
            data-testid="portfolio-headline"
          >
            Real jobs. Real transformations.
          </h2>
          <p className="mt-5 text-base md:text-lg text-ohana-text-muted leading-relaxed">
            Every cleanout below is a real Ohana job in Central Indiana. Sorted, hauled, donated where possible, and finished broom-clean. The wider gallery grows with every property we transition.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {GALLERY.map((item) => (
            <article
              key={item.id}
              className="overflow-hidden rounded-2xl bg-white border border-ohana-border shadow-sm hover:shadow-lg transition-shadow"
              data-testid={`portfolio-card-${item.id}`}
            >
              <div className="aspect-square w-full overflow-hidden bg-ohana-surface">
                <img
                  src={item.src}
                  alt={item.alt}
                  loading="lazy"
                  className="h-full w-full object-cover"
                  data-testid={`portfolio-image-${item.id}`}
                />
              </div>
              <div className="px-6 py-6">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ohana-text-muted">
                  {item.location}
                </p>
                <h3 className="mt-2 font-serif text-2xl text-ohana-primary leading-snug">
                  {item.label}
                </h3>
                <p className="mt-3 text-sm md:text-base text-ohana-text leading-relaxed">
                  {item.body}
                </p>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {item.services.map((s) => (
                    <li
                      key={s}
                      className="inline-flex items-center rounded-full bg-ohana-primary/5 text-ohana-primary text-xs font-medium px-3 py-1"
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-5 rounded-2xl bg-ohana-primary text-ohana-bg px-6 py-7 md:px-10">
          <p className="font-serif text-xl md:text-2xl leading-snug max-w-2xl">
            Want a transformation like this on your property?
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center rounded-full bg-ohana-accent text-ohana-primary font-semibold px-6 py-3 hover:bg-white transition whitespace-nowrap"
            data-testid="portfolio-cta-quote"
          >
            Get My Free Quote
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
